import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TID } from "@/constants/testIds";
import { Upload, Plus, Trash2, Save, FileText } from "lucide-react";

const SHIFT_OPTS = ["Day", "Evening", "Night", "Weekend"];
const WORKTYPE_OPTS = ["Full-time","Part-time","Temporary","Contract","Seasonal","Remote","Hybrid","On-site"];

export default function CandidateProfile() {
  const [prof, setProf] = useState(null);
  const [industries, setIndustries] = useState([]);
  const fileRef = useRef();

  useEffect(() => {
    api.get("/candidates/me").then(r => setProf(r.data));
    api.get("/admin/industries").then(r => setIndustries(r.data.items || []));
  }, []);

  if (!prof) return <div className="text-slate-500">Loading…</div>;

  const set = (patch) => setProf({ ...prof, ...patch });

  const save = async () => {
    const { user, id, ...rest } = prof;
    try {
      await api.put("/candidates/me", rest);
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); }
  };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    try {
      await api.post("/files/upload?purpose=resume", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Resume uploaded");
      const { data } = await api.get("/candidates/me");
      setProf(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const toggleArr = (key, value) => {
    const arr = prof[key] || [];
    set({ [key]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] });
  };

  const addSkill = (k, val) => {
    if (!val) return;
    set({ [k]: [...(prof[k] || []), val] });
  };

  const removeAt = (k, i) => {
    const arr = [...(prof[k] || [])]; arr.splice(i, 1); set({ [k]: arr });
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6 flex justify-between items-end flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">Candidate</div>
          <h1 className="font-display text-3xl font-bold text-navy">My Profile</h1>
        </div>
        <button onClick={save} className="btn-navy"><Save className="w-4 h-4" /> Save changes</button>
      </div>

      {/* Resume */}
      <div className="border border-slate-200 rounded-xl bg-white p-6 mb-6">
        <div className="eyebrow mb-3">Resume</div>
        {prof.resume_filename ? (
          <div className="flex items-center gap-3 mb-3">
            <FileText className="w-5 h-5 text-navy" />
            <div className="text-sm text-navy">{prof.resume_filename}</div>
          </div>
        ) : (
          <div className="text-sm text-slate-500 mb-3">No resume uploaded yet.</div>
        )}
        <input ref={fileRef} type="file" accept=".pdf,.docx,.doc" onChange={upload} className="hidden" data-testid={TID.candidateResumeUpload} />
        <button onClick={() => fileRef.current?.click()} className="btn-navy py-2 px-4 text-sm"><Upload className="w-4 h-4" /> Upload resume (PDF/DOCX)</button>
      </div>

      {/* Basics */}
      <div className="border border-slate-200 rounded-xl bg-white p-6 space-y-4 mb-6">
        <div className="eyebrow">Basics</div>
        <div>
          <label className="text-xs text-slate-500">Headline</label>
          <input value={prof.headline} onChange={e => set({ headline: e.target.value })}
            className="w-full border rounded px-3 py-2 mt-1 text-sm" placeholder="e.g. Warehouse Team Lead — 6 yrs" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Phone</label>
            <input value={prof.phone} onChange={e => set({ phone: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500">City</label>
            <input value={prof.city} onChange={e => set({ city: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500">State</label>
            <input value={prof.state} onChange={e => set({ state: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Work authorization</label>
            <select value={prof.work_authorization} onChange={e => set({ work_authorization: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm">
              <option value="">Select…</option>
              {["US Citizen","Permanent Resident","H1B","F1 OPT","EAD","Other"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Transportation</label>
            <select value={prof.transportation} onChange={e => set({ transportation: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm">
              <option value="">Select…</option>
              {["Own vehicle","Public transit","Rideshare","None"].map(o => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Availability</label>
            <input value={prof.availability} onChange={e => set({ availability: e.target.value })} placeholder="e.g. 2 weeks notice" className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="border border-slate-200 rounded-xl bg-white p-6 space-y-4 mb-6">
        <div className="eyebrow">Preferences</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-500">Pay type</label>
            <select value={prof.pay_type} onChange={e => set({ pay_type: e.target.value })} className="w-full border rounded px-3 py-2 mt-1 text-sm">
              <option value="hourly">Hourly</option><option value="salary">Salary</option>
            </select>
          </div>
          <div />
          <div>
            <label className="text-xs text-slate-500">Salary/rate min</label>
            <input type="number" value={prof.salary_min ?? ""} onChange={e => set({ salary_min: parseFloat(e.target.value) || null })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
          <div>
            <label className="text-xs text-slate-500">Salary/rate max</label>
            <input type="number" value={prof.salary_max ?? ""} onChange={e => set({ salary_max: parseFloat(e.target.value) || null })} className="w-full border rounded px-3 py-2 mt-1 text-sm" />
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500">Preferred shifts</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {SHIFT_OPTS.map(s => (
              <button key={s} type="button" onClick={() => toggleArr("shift_prefs", s)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${(prof.shift_prefs || []).includes(s) ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600 hover:border-navy"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500">Work type</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {WORKTYPE_OPTS.map(s => (
              <button key={s} type="button" onClick={() => toggleArr("work_type_prefs", s)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${(prof.work_type_prefs || []).includes(s) ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600 hover:border-navy"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500">Industries of interest</label>
          <div className="flex flex-wrap gap-2 mt-1">
            {industries.map(i => (
              <button key={i.id} type="button" onClick={() => toggleArr("industries", i.name)}
                className={`px-3 py-1 rounded-full text-xs border transition-colors ${(prof.industries || []).includes(i.name) ? "bg-navy text-white border-navy" : "border-slate-200 text-slate-600 hover:border-navy"}`}>{i.name}</button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-xs text-slate-500">Desired job titles</label>
          <TagInput items={prof.desired_titles || []} onAdd={v => addSkill("desired_titles", v)} onRemove={i => removeAt("desired_titles", i)} />
        </div>
      </div>

      {/* Skills / certs */}
      <div className="border border-slate-200 rounded-xl bg-white p-6 space-y-4 mb-6">
        <div className="eyebrow">Skills & Certifications</div>
        <div>
          <label className="text-xs text-slate-500">Skills</label>
          <TagInput items={prof.skills || []} onAdd={v => addSkill("skills", v)} onRemove={i => removeAt("skills", i)} />
        </div>
        <div>
          <label className="text-xs text-slate-500">Certifications</label>
          <TagInput items={prof.certifications || []} onAdd={v => addSkill("certifications", v)} onRemove={i => removeAt("certifications", i)} />
        </div>
      </div>

      {/* Experience & Education */}
      <ListEditor
        title="Experience"
        items={prof.experience || []}
        blank={{ title: "", company: "", start: "", end: "", description: "" }}
        fields={[
          { k: "title", label: "Title" }, { k: "company", label: "Company" },
          { k: "start", label: "Start" }, { k: "end", label: "End" },
          { k: "description", label: "Description", multi: true },
        ]}
        onChange={(arr) => set({ experience: arr })}
      />
      <ListEditor
        title="Education"
        items={prof.education || []}
        blank={{ school: "", degree: "", field: "", year: "" }}
        fields={[
          { k: "school", label: "School" }, { k: "degree", label: "Degree" },
          { k: "field", label: "Field" }, { k: "year", label: "Year" },
        ]}
        onChange={(arr) => set({ education: arr })}
      />
      <ListEditor
        title="References"
        items={prof.references || []}
        blank={{ name: "", relationship: "", phone: "", email: "" }}
        fields={[
          { k: "name", label: "Name" }, { k: "relationship", label: "Relationship" },
          { k: "phone", label: "Phone" }, { k: "email", label: "Email" },
        ]}
        onChange={(arr) => set({ references: arr })}
      />

      <button onClick={save} className="btn-navy w-full justify-center mt-4"><Save className="w-4 h-4" /> Save all changes</button>
    </div>
  );
}

function TagInput({ items, onAdd, onRemove }) {
  const [v, setV] = React.useState("");
  return (
    <div>
      <div className="flex gap-2 mt-1">
        <input value={v} onChange={e => setV(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); onAdd(v); setV(""); } }}
          className="flex-1 border rounded px-3 py-2 text-sm" placeholder="Type and press Enter" />
        <button type="button" onClick={() => { onAdd(v); setV(""); }} className="btn-navy py-2 px-3"><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {items.map((s, i) => (
          <span key={i} className="px-2.5 py-1 rounded-full bg-slate-100 text-xs inline-flex items-center gap-1">
            {s} <button onClick={() => onRemove(i)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

function ListEditor({ title, items, blank, fields, onChange }) {
  const add = () => onChange([...items, { ...blank }]);
  const remove = (i) => { const a = [...items]; a.splice(i, 1); onChange(a); };
  const set = (i, k, v) => { const a = [...items]; a[i] = { ...a[i], [k]: v }; onChange(a); };
  return (
    <div className="border border-slate-200 rounded-xl bg-white p-6 mb-6">
      <div className="flex justify-between items-center mb-3">
        <div className="eyebrow">{title}</div>
        <button onClick={add} className="text-xs text-navy hover:text-gold inline-flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add</button>
      </div>
      {items.map((it, i) => (
        <div key={i} className="border border-slate-100 rounded-lg p-3 mb-3 grid grid-cols-2 gap-2">
          {fields.map(f => (
            <div key={f.k} className={f.multi ? "col-span-2" : ""}>
              <label className="text-[11px] text-slate-500">{f.label}</label>
              {f.multi ? (
                <textarea rows={2} value={it[f.k] || ""} onChange={e => set(i, f.k, e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
              ) : (
                <input value={it[f.k] || ""} onChange={e => set(i, f.k, e.target.value)} className="w-full border rounded px-2 py-1 text-sm" />
              )}
            </div>
          ))}
          <div className="col-span-2 text-right">
            <button onClick={() => remove(i)} className="text-xs text-red-500 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Remove</button>
          </div>
        </div>
      ))}
      {items.length === 0 && <div className="text-sm text-slate-500">Nothing added yet.</div>}
    </div>
  );
}
